import { useState } from 'react';
import { useAppointments, useSaveAppointment, useDeleteAppointment, Appointment } from '@/features/appointments/hooks/useAppointments';
import { useClients } from '@/features/clients/hooks/useClients';
import { motion } from 'framer-motion';
import { CalendarDays, Plus, Trash2, Edit, DollarSign, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { format, parseISO, startOfWeek, addDays, isSameDay } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'sonner';

const Appointments = () => {
  const { data: appointments = [], isLoading } = useAppointments();
  const saveAppt = useSaveAppointment();
  const deleteAppt = useDeleteAppointment();
  const { clients } = useClients();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Partial<Appointment> | null>(null);
  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));

  const openNew = () => {
    setEditing({ date: selectedDate, start_time: '10:00', duration_minutes: 60, price: 0, is_paid: false, status: 'scheduled', client_name: '' });
    setDialogOpen(true);
  };
  const openEdit = (a: Appointment) => { setEditing(a); setDialogOpen(true); };

  const handleSave = () => {
    if (!editing?.client_name || !editing?.date || !editing?.start_time) {
      toast.error('Remplissez les champs obligatoires');
      return;
    }
    saveAppt.mutate(editing as any, {
      onSuccess: () => { setDialogOpen(false); toast.success('Rendez-vous enregistré'); },
      onError: () => toast.error('Erreur'),
    });
  };

  // Week view
  const weekStart = startOfWeek(parseISO(selectedDate), { weekStartsOn: 1 });
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  const filteredAppts = appointments.filter(a => a.date === selectedDate);

  return (
    <div className="p-4 lg:p-8 max-w-6xl mx-auto space-y-6">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold font-display text-foreground flex items-center gap-3">
            <CalendarDays className="text-primary" size={28} />
            Rendez-vous
          </h1>
          <p className="text-muted-foreground mt-1">Gérez vos rendez-vous clients</p>
        </div>
        <Button onClick={openNew} className="gap-2">
          <Plus size={16} /> Nouveau
        </Button>
      </motion.div>

      {/* Week selector */}
      <div className="grid grid-cols-7 gap-2">
        {weekDays.map(day => {
          const dayStr = format(day, 'yyyy-MM-dd');
          const dayAppts = appointments.filter(a => a.date === dayStr);
          const isSelected = dayStr === selectedDate;
          const isToday = isSameDay(day, new Date());
          return (
            <button
              key={dayStr}
              onClick={() => setSelectedDate(dayStr)}
              className={`p-3 rounded-xl text-center transition-all ${
                isSelected ? 'bg-primary text-primary-foreground' :
                isToday ? 'bg-primary/10 text-primary' :
                'bg-secondary/30 text-muted-foreground hover:bg-secondary/60'
              }`}
            >
              <p className="text-xs font-medium capitalize">{format(day, 'EEE', { locale: fr })}</p>
              <p className="text-lg font-bold">{format(day, 'd')}</p>
              {dayAppts.length > 0 && (
                <div className={`mt-1 w-2 h-2 rounded-full mx-auto ${isSelected ? 'bg-primary-foreground' : 'bg-primary'}`} />
              )}
            </button>
          );
        })}
      </div>

      {/* Day view */}
      <div className="space-y-3">
        <h2 className="text-lg font-semibold text-foreground capitalize">
          {format(parseISO(selectedDate), 'EEEE d MMMM yyyy', { locale: fr })}
        </h2>
        {filteredAppts.length === 0 ? (
          <div className="glass-card p-12 text-center">
            <CalendarDays size={40} className="mx-auto mb-3 text-muted-foreground opacity-30" />
            <p className="text-muted-foreground">Aucun rendez-vous ce jour</p>
          </div>
        ) : (
          filteredAppts.map(appt => (
            <motion.div
              key={appt.id}
              initial={{ opacity: 0, x: -12 }}
              animate={{ opacity: 1, x: 0 }}
              className="glass-card p-4 flex items-center justify-between"
            >
              <div className="flex items-center gap-4">
                <div className="px-3 py-2 rounded-xl bg-primary/10 text-primary font-bold text-lg">
                  {appt.start_time?.slice(0, 5)}
                </div>
                <div>
                  <p className="font-semibold text-foreground">{appt.client_name}</p>
                  <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1"><Clock size={12} /> {appt.duration_minutes} min</span>
                    {appt.project_name && <span>• {appt.project_name}</span>}
                    {appt.price > 0 && <span className="flex items-center gap-1"><DollarSign size={12} /> {appt.price}€</span>}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {!appt.is_paid && appt.price > 0 && (
                  <span className="text-xs px-2 py-1 rounded-full bg-destructive/20 text-destructive font-medium">Non payé</span>
                )}
                <Button variant="ghost" size="icon" onClick={() => openEdit(appt)}>
                  <Edit size={14} />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => {
                  if (confirm('Supprimer ce rendez-vous ?')) deleteAppt.mutate(appt.id);
                }}>
                  <Trash2 size={14} className="text-destructive" />
                </Button>
              </div>
            </motion.div>
          ))
        )}
      </div>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editing?.id ? 'Modifier' : 'Nouveau'} rendez-vous</DialogTitle>
            <DialogDescription className="sr-only">Formulaire de rendez-vous</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Client *</Label>
              <Input value={editing?.client_name || ''} onChange={e => setEditing(prev => ({ ...prev!, client_name: e.target.value }))} placeholder="Nom du client" />
            </div>
            <div>
              <Label>Projet</Label>
              <Input value={editing?.project_name || ''} onChange={e => setEditing(prev => ({ ...prev!, project_name: e.target.value }))} placeholder="Ex: Tigre style japonais" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Date *</Label>
                <Input type="date" value={editing?.date || ''} onChange={e => setEditing(prev => ({ ...prev!, date: e.target.value }))} />
              </div>
              <div>
                <Label>Heure *</Label>
                <Input type="time" value={editing?.start_time || ''} onChange={e => setEditing(prev => ({ ...prev!, start_time: e.target.value }))} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Durée (min)</Label>
                <Input type="number" value={editing?.duration_minutes || 60} onChange={e => setEditing(prev => ({ ...prev!, duration_minutes: parseInt(e.target.value) || 60 }))} />
              </div>
              <div>
                <Label>Prix (€)</Label>
                <Input type="number" value={editing?.price || 0} onChange={e => setEditing(prev => ({ ...prev!, price: parseFloat(e.target.value) || 0 }))} />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={editing?.is_paid || false} onCheckedChange={v => setEditing(prev => ({ ...prev!, is_paid: v }))} />
              <Label>Payé</Label>
            </div>
            <div>
              <Label>Notes</Label>
              <Input value={editing?.notes || ''} onChange={e => setEditing(prev => ({ ...prev!, notes: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
            <Button onClick={handleSave} disabled={saveAppt.isPending}>Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Appointments;
